import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Draggable from 'react-draggable';
import { CheckCircle, KeyboardDoubleArrowDown, AutoFixHigh, DragIndicator } from '@mui/icons-material';
import socket, { postRequest, showMessage } from '../../config';
import { toggleModal } from '../../reducers/modalReducer';

const barBg = { background: '#E5E7EB', borderRadius: '10px', height: '8px', overflow: 'hidden', marginBottom: '0.75rem' };
const barFill = (w) => ({ background: 'linear-gradient(90deg, #4a67ff 0%, #3d56d9 100%)', height: '100%', borderRadius: '10px', width: `${w}%`, transition: 'width 0.3s ease' });
const ellipsis = { margin: 0, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

// Minimized Auditor (diagnose) status pill — same visual pattern as AutoFillStatusBar but scoped to a single flow,
// since diagnose only has two states (running / completed) rather than autofill's many sequential phases
const DiagnoseStatusBar = () => {
    const [isExpanded, setIsExpanded] = useState(true);
    const nodeRef = useRef(null);
    const dispatch = useDispatch();
    const modal = useSelector(state => state.modal);
    const [status, setStatus] = useState({ total: 0, current: 0, correct: 0, incorrect: 0, qNo: '', questionText: '' });

    const visible = !!modal.diagnoseStatusBarVisible;
    const completed = !!modal.diagnoseStatusBarCompleted;

    useEffect(() => {
        const companyId = modal.diagnoseCompanyId;
        const onProgress = (data) => { if (data.companyId === companyId) setStatus(s => ({ ...s, total: data.total || 0, current: data.current || 0, correct: data.correct || 0, incorrect: data.incorrect || 0 })); };
        const onReading = (data) => { if (data.companyId === companyId) setStatus(s => ({ ...s, qNo: data.qNo || '', questionText: data.questionText || '' })); };
        const onCompleted = (data) => {
            if (data.companyId && data.companyId !== companyId) return;
            dispatch(toggleModal({ diagnoseStatusBarCompleted: true }));
        };
        const onCancelled = () => dispatch(toggleModal({ diagnoseStatusBarVisible: false, diagnoseStatusBarCompleted: false }));
        socket.on('diagnose:progress', onProgress);
        socket.on('diagnose:reading', onReading);
        socket.on('diagnose:completed', onCompleted);
        socket.on('diagnose:cancelled', onCancelled);
        return () => {
            socket.off('diagnose:progress', onProgress);
            socket.off('diagnose:reading', onReading);
            socket.off('diagnose:completed', onCompleted);
            socket.off('diagnose:cancelled', onCancelled);
        };
    }, [modal.diagnoseCompanyId, dispatch]);

    if (!visible || modal.open === 'diagnoseReport') return null;

    const pct = status.total > 0 ? Math.round((status.current / status.total) * 100) : 0;

    const handleCancel = async () => {
        dispatch(toggleModal({ diagnoseCancelled: true }));
        postRequest('/clarence/diagnose/cancel', {}).catch(() => { });
        dispatch(toggleModal({ diagnoseLoading: false, diagnoseStatusBarVisible: false, diagnoseStatusBarCompleted: false }));
        showMessage('Auditor cancelled');
    };

    const handleReview = () => dispatch(toggleModal({ open: 'diagnoseReport', diagnoseStatusBarVisible: false, diagnoseStatusBarCompleted: false }));

    return (
        <Draggable nodeRef={nodeRef} handle=".diagnose-drag-handle" bounds="parent">
            <div ref={nodeRef} className='pf bg1 shadow' style={{ zIndex: '10000', right: '10px', bottom: '80px', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', width: '340px' }}>
                {/* Header */}
                <div className="df aic jcsb w100 pl1rem diagnose-drag-handle" style={{ height: '60px', borderBottom: isExpanded ? '1px solid #eaeaea' : 'none', cursor: 'grab' }}>
                    <div className="df aic" style={{ gap: '8px', flex: 1, overflow: 'hidden' }}>
                        <DragIndicator style={{ fontSize: '18px', color: '#9CA3AF', flexShrink: 0 }} />
                        {completed
                            ? <CheckCircle style={{ fontSize: '20px', color: '#059669' }} />
                            : <AutoFixHigh style={{ fontSize: '20px' }} className="auto-fill-status-spin" />
                        }
                        <h3 className="c15 fs14px" style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {completed ? 'Auditor completed' : (status.total > 0 ? `Verifying ${status.current} of ${status.total} answers` : 'Auditor verifying answers')}
                        </h3>
                    </div>
                    <div className="df aic jcfe">
                        <div className="cp px0-5rem c15 df aic" onClick={handleReview} title={completed ? 'Review results' : 'Restore to full modal'}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M21 11V3H13L16.29 6.29L6.29 16.29L3 13V21H11L7.71 17.71L17.71 7.71L21 11Z" fill="#374151" />
                            </svg>
                        </div>
                        <div className="cp px0-5rem c15 df aic" onClick={() => setIsExpanded(!isExpanded)} style={{ transition: 'transform 0.3s ease', transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)' }}>
                            <KeyboardDoubleArrowDown />
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="w100" style={{ maxHeight: isExpanded ? '220px' : '0px', overflowY: 'auto', transition: 'max-height 0.3s ease, opacity 0.3s ease', opacity: isExpanded ? 1 : 0 }}>
                    <div style={{ padding: '1rem' }}>
                        {completed ? (
                            <div style={{ textAlign: 'center' }}>
                                <p className="c15 fw500 fs12px" style={{ margin: '0 0 0.75rem' }}>
                                    {status.total > 0 ? `${status.correct} correct · ${status.incorrect} need review` : 'Report ready to review.'}
                                </p>
                                <button onClick={handleReview} className="bg3 bg4-hover c1 br10px px2rem py0-5rem cp fw500 fs12px">Click to Review</button>
                            </div>
                        ) : (<>
                            <ProgressBar percent={pct} />
                            <p className="c15 fw500 fs12px" style={{ margin: '0 0 0.25rem', textAlign: 'center' }}>{status.total > 0 ? `${pct}% complete` : 'Starting up…'}</p>
                            {status.qNo && <p className="c27 fs11px" style={ellipsis}>Checking: {status.qNo}</p>}
                            {status.questionText && <p className="c15 fs11px" style={{ ...ellipsis, margin: '0.25rem 0' }}>"{status.questionText}"</p>}
                            <div className="df aic jcc" style={{ marginTop: '0.75rem' }}>
                                <button onClick={handleCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', fontSize: '12px', textDecoration: 'underline', padding: 0 }}>Cancel</button>
                            </div>
                        </>)}
                    </div>
                </div>
            </div>
        </Draggable>
    );
};

const ProgressBar = ({ percent }) => (<div style={barBg}><div style={barFill(percent)} /></div>);

export default DiagnoseStatusBar;
